// GuestChat.tsx — the anonymous-visitor chat surface (S4 4.G2).
//
// A prospect on a white-label org's subdomain (<slug>.drishalom.com) who tapped
// "כניסה כאורחים" lands here. Deliberately MUCH simpler than the member Chat: no
// auth-gate, no SSE stream, no history hydration, no upload, no impersonation.
// Guest Q&A is a plain SYNCHRONOUS POST /guest-chat → { text } (the read-only
// guest agent answers from the org KB); the conversation lives in memory and is
// replayed to the backend via `history`. Branded through the CSS custom
// properties applied at boot by brand.ts. "להצטרף" clears the guest session and
// routes to the login / onboarding surface.

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getOrgConfig } from '../brand.ts';

const MAX_MESSAGE_LEN = 10_000;
// Mirror the backend's MAX_HISTORY_TURNS — only the recent window is replayed.
const HISTORY_TURNS = 12;

type GuestTurn = {
  role: 'user' | 'assistant';
  id: string;
  text: string;
  pending?: boolean;
};

const SUGGESTIONS = ['מה זה?', 'איך זה עובד?', 'מה מקבלים?'];

export const GuestChat = () => {
  const cfg = getOrgConfig();
  const orgName = cfg.brand?.name ?? cfg.name ?? 'דרישלום';

  const [turns, setTurns] = useState<GuestTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({
      top: scroller.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns.length, pending]);

  const send = async (text: string): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    setDraft('');
    setError(null);
    setPending(true);

    const userTurn: GuestTurn = {
      role: 'user',
      id: crypto.randomUUID(),
      text: trimmed,
    };
    const pendingId = crypto.randomUUID();
    // The history the backend sees = the turns BEFORE this user message.
    const history = turns
      .filter((t) => !t.pending)
      .slice(-HISTORY_TURNS)
      .map((t) => ({ role: t.role, text: t.text }));
    setTurns((t) => [
      ...t,
      userTurn,
      { role: 'assistant', id: pendingId, text: '', pending: true },
    ]);

    try {
      const res = await fetch('/guest-chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message: trimmed, history }),
        credentials: 'include',
      });
      if (res.status === 401) {
        // The 2h guest session lapsed — send them back to the entry surface.
        window.location.assign('/');
        return;
      }
      if (res.status === 429) {
        setTurns((t) => t.filter((turn) => turn.id !== pendingId));
        setError('יותר מדי הודעות. המתינו רגע ונסו שוב.');
        setPending(false);
        return;
      }
      if (!res.ok) throw new Error(`http_${res.status}`);
      const data = (await res.json()) as { text?: string };
      const reply = typeof data.text === 'string' ? data.text : '';
      setTurns((t) =>
        t.map((turn) =>
          turn.id === pendingId
            ? { role: 'assistant', id: pendingId, text: reply }
            : turn,
        ),
      );
    } catch {
      // Replace the dots with a retryable error bubble.
      setTurns((t) =>
        t.map((turn) =>
          turn.id === pendingId
            ? { role: 'assistant', id: pendingId, text: 'שגיאת רשת. נסו שוב.' }
            : turn,
        ),
      );
    } finally {
      setPending(false);
    }
  };

  // "להצטרף" — join the org. S4 4.W3: when the org offers web
  // self-onboarding, promote this guest session to a placeholder member IN PLACE
  // (POST /onboard/start fills session_tokens.user_id on the same cookie — no
  // re-auth, conversation continuity) → reload re-probes /me (`onboarding: true`)
  // → OnboardingChat. When self-onboarding is OFF, fall back to the prior
  // behavior: clear the guest session and land on the login surface.
  const join = async (): Promise<void> => {
    if (cfg.self_onboard_enabled) {
      try {
        const res = await fetch('/onboard/start', {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          window.location.assign('/');
          return;
        }
      } catch {
        /* fall through to the logout path below */
      }
    }
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      /* best-effort — the reload re-probes /me regardless */
    }
    window.location.assign('/');
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-card)] px-4">
        <h1 className="text-base font-bold text-[var(--color-primary)]">
          {orgName}
        </h1>
        <button
          type="button"
          onClick={() => void join()}
          className="cursor-pointer rounded-full bg-[var(--color-primary)] px-4 py-1.5 text-xs font-bold text-white transition-colors hover:opacity-90"
        >
          להצטרף
        </button>
      </header>

      {/* S5 — say plainly that this is a guest session and nothing is
          kept. Under the header rather than in the empty state, so it stays
          visible once the visitor starts asking questions. */}
      <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-primary-light)] px-4 py-2 text-center text-xs text-[var(--color-muted)]">
        אתם בתצוגת אורח — השיחה לא נשמרת. הצטרפו כדי לשמור אותה.
      </div>

      <main
        ref={scroller}
        className="flex-1 overflow-y-auto px-4 py-3"
        aria-live="polite"
      >
        <div className="mx-auto max-w-[720px]">
          {turns.length === 0 ? (
            <div className="flex flex-col items-center gap-3 pt-12 text-center">
              <div className="text-2xl font-bold text-[var(--color-primary)]">
                {orgName}
              </div>
              <div className="mt-2 text-base text-[var(--color-muted)]">
                שמחים שהגעתם 👋 שאלו אותי על התוכנית
              </div>
              <div className="mt-4 flex w-full max-w-sm flex-col gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-sm hover:bg-[var(--color-primary-light)]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <ul className="space-y-3">
              {turns.map((t) => (
                <GuestBubble key={t.id} turn={t} />
              ))}
            </ul>
          )}
          {error ? (
            <div
              role="alert"
              className="mx-auto mt-3 max-w-[85%] rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-center text-sm text-[var(--color-bad)]"
            >
              {error}
            </div>
          ) : null}
        </div>
      </main>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
        className="border-t border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2"
      >
        <div className="mx-auto flex max-w-[720px] items-stretch gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_LEN))}
            maxLength={MAX_MESSAGE_LEN}
            placeholder="שאלו משהו..."
            aria-label="הודעה"
            className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-start outline-none focus:border-[var(--color-primary)]"
            dir="rtl"
            autoFocus
          />
          <button
            type="submit"
            disabled={!draft.trim() || pending}
            className="cursor-pointer rounded-lg bg-[var(--color-primary)] px-4 text-sm font-bold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[var(--color-border)] disabled:text-[var(--color-muted)] disabled:opacity-100 disabled:hover:opacity-100"
          >
            שלחו
          </button>
        </div>
      </form>
    </div>
  );
};

const GuestBubble = ({ turn }: { turn: GuestTurn }) => {
  const isUser = turn.role === 'user';
  // Match the member chat alignment: user visual-RIGHT, agent visual-LEFT.
  const align = isUser ? 'flex justify-start' : 'flex justify-end';
  if (!isUser && turn.pending) {
    return (
      <li className={`${align} items-end gap-2`}>
        <div
          className="max-w-[85%] rounded-2xl bg-[var(--color-primary-light)] px-4 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-bubble)]"
          role="status"
          aria-label="חושבת..."
        >
          <span className="inline-flex gap-1" aria-hidden="true">
            <span className="thinking-dot [animation-delay:0ms]" />
            <span className="thinking-dot [animation-delay:150ms]" />
            <span className="thinking-dot [animation-delay:300ms]" />
          </span>
        </div>
      </li>
    );
  }
  return (
    <li className={`${align} items-end gap-2`}>
      <div
        className={
          isUser
            ? 'max-w-[85%] rounded-2xl bg-[var(--color-user-bubble)] px-4 py-2 text-sm text-white shadow-[var(--shadow-bubble)]'
            : 'max-w-[85%] rounded-2xl bg-[var(--color-primary-light)] px-4 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-bubble)]'
        }
      >
        {isUser ? (
          <div>{turn.text}</div>
        ) : (
          <div className="[&_p]:my-1 [&_strong]:font-bold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:ps-5 [&_a]:text-[var(--color-primary)] [&_a]:underline [&>:first-child]:mt-0 [&>:last-child]:mb-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.text}</ReactMarkdown>
          </div>
        )}
      </div>
    </li>
  );
};
